import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaShieldAlt } from 'react-icons/fa';
import authApi from '../api/authApi';
import PubSub from 'pubsub-js';

const BackupCodesPanel = ({ codes, onClose }) => {
  if (!codes?.length) return null;
  return (
    <Alert variant="warning" className="text-start">
      <strong>Save these backup codes now.</strong> Each can be used once if you
      lose access to your authenticator.
      <ul className="mb-2 mt-2 font-monospace">
        {codes.map(code => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <Button size="sm" variant="outline-dark" onClick={onClose}>
        I have saved these codes
      </Button>
    </Alert>
  );
};

/**
 * MFA enrollment / management for the signed-in user (Profile).
 * Policy comes from tenant session_setting.mfa_policy: off | optional | required.
 */
const MfaSettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showDisable, setShowDisable] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await authApi.mfaStatus();
      if (result.success) {
        setStatus(result);
      } else {
        setError(result.errors || 'Unable to load MFA status');
      }
    } catch (err) {
      setError(err.message || 'Unable to load MFA status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const startSetup = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await authApi.mfaSetup();
      if (result.success) {
        setSetup(result);
        setCode('');
      } else {
        setError(result.errors || 'Failed to start MFA setup');
      }
    } catch (err) {
      setError(err.message || 'Failed to start MFA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async e => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await authApi.mfaEnable(code);
      if (result.success) {
        setBackupCodes(result.backupCodes || []);
        setSetup(null);
        setCode('');
        PubSub.publish('RECORD_SAVED_TOAST', {
          title: 'MFA enabled',
          message: 'Authenticator app linked successfully.',
        });
        await loadStatus();
      } else {
        setError(result.errors || 'Invalid code');
      }
    } catch (err) {
      setError(err.message || 'Failed to enable MFA');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async e => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await authApi.mfaDisable(
        disableForm.password,
        disableForm.code
      );
      if (result.success) {
        setShowDisable(false);
        setDisableForm({ password: '', code: '' });
        PubSub.publish('RECORD_SAVED_TOAST', {
          title: 'MFA disabled',
          message: 'Two-factor authentication has been turned off.',
        });
        await loadStatus();
      } else {
        setError(result.errors || 'Failed to disable MFA');
      }
    } catch (err) {
      setError(err.message || 'Failed to disable MFA');
    } finally {
      setBusy(false);
    }
  };

  const handleRegen = async e => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await authApi.mfaRegenerateBackupCodes(regenCode);
      if (result.success) {
        setShowRegen(false);
        setRegenCode('');
        setBackupCodes(result.backupCodes || []);
        await loadStatus();
      } else {
        setError(result.errors || 'Failed to regenerate backup codes');
      }
    } catch (err) {
      setError(err.message || 'Failed to regenerate backup codes');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-muted py-2">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading MFA…
      </div>
    );
  }

  if (!status || status.policy === 'off') {
    return null;
  }

  return (
    <div className="mt-4 text-start border-top pt-3">
      <h6 className="mb-2" style={{ color: '#002B5B' }}>
        <FaShieldAlt className="me-2" />
        Two-factor authentication (MFA)
      </h6>
      <p className="text-muted small mb-2">
        Policy: <strong>{status.policy}</strong>
        {status.enabled
          ? ` · Enabled${status.enrolledAt
            ? ` on ${new Date(status.enrolledAt).toLocaleDateString()}`
            : ''
          } · ${status.unusedBackupCodes} backup codes left`
          : ' · Not enabled'}
      </p>
      <p className="text-muted small mb-2">
        Lost your phone or backup codes? Contact your administrator.
        Lost your phone or backup codes? Contact your administrator.
      </p>
      {error && (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      )}

      <BackupCodesPanel
        codes={backupCodes}
        onClose={() => setBackupCodes(null)}
      />

      {!status.enabled && status.canEnroll && !setup && (
        <Button
          variant="outline-primary"
          size="sm"
          disabled={busy}
          onClick={startSetup}
        >
          Set up authenticator app
        </Button>
      )}

      {setup && (
        <Form onSubmit={confirmEnable} className="mt-2">
          <p className="small">
            Scan this QR code with Google Authenticator, Authy, or a similar
            app, then enter the 6-digit code.
          </p>
          {setup.qrCodeDataUrl && (
            <img
              src={setup.qrCodeDataUrl}
              alt="MFA QR code"
              className="mfa-qr-code mb-2 d-block mx-auto"
              width={200}
              height={200}
            />
          )}
          <p className="small text-muted">
            Or enter key manually:{' '}
            <code className="user-select-all">{setup.secret}</code>
          </p>
          <Form.Group className="mb-2">
            <Form.Control
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value.trim())}
              required
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button type="submit" size="sm" disabled={busy || code.length < 6}>
              {busy ? 'Verifying…' : 'Enable MFA'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline-secondary"
              onClick={() => setSetup(null)}
            >
              Cancel
            </Button>
          </div>
        </Form>
      )}

      {status.enabled && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {status.canDisable && (
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setShowDisable(true)}
            >
              Disable MFA
            </Button>
          )}
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setShowRegen(true)}
          >
            Regenerate backup codes
          </Button>
        </div>
      )}

      <Modal show={showDisable} onHide={() => setShowDisable(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Disable MFA</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleDisable}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={disableForm.password}
                onChange={e =>
                  setDisableForm(prev => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Authenticator or backup code</Form.Label>
              <Form.Control
                type="text"
                value={disableForm.code}
                onChange={e =>
                  setDisableForm(prev => ({ ...prev, code: e.target.value }))
                }
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDisable(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" disabled={busy}>
              Disable
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showRegen} onHide={() => setShowRegen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Regenerate backup codes</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleRegen}>
          <Modal.Body>
            <p className="small text-muted">
              This invalidates existing unused backup codes. Enter a current
              authenticator or backup code to continue.
            </p>
            <Form.Control
              type="text"
              value={regenCode}
              onChange={e => setRegenCode(e.target.value)}
              placeholder="Authenticator or backup code"
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRegen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              Generate
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MfaSettings;
